// src/components/guides/QuickGuideHub.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lightbulb,
  Zap,
  X,
  ChevronRight,
  ChevronLeft,
  Star,
  MessageSquare,
  Target,
  Coffee,
  Brain,
  Users,
  BookOpen,
  Mic,
  Sparkles,
  Award,
  TrendingUp,
  Heart
} from 'lucide-react';
import Card from '../shared/Card';
import Button from '../shared/Button';
import {
  StepsLayout,
  TypesLayout,
  TechniquesLayout,
  HacksLayout,
  CategoriesLayout,
  BlocksLayout
} from './QuickGuideLayouts';
import { CRISIS_HELP } from '../../data/crisisSupport';

const QuickGuideHub = ({ isOpen, onClose }) => {
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [favouriteGuides, setFavouriteGuides] = useState([]);
  const [copiedItem, setCopiedItem] = useState(null);

  const copyToClipboard = (text, itemId) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(itemId);
    setTimeout(() => setCopiedItem(null), 2000);
  };

  const toggleFavourite = (guideId) => {
    if (favouriteGuides.includes(guideId)) {
      setFavouriteGuides(favouriteGuides.filter(id => id !== guideId));
    } else {
      setFavouriteGuides([...favouriteGuides, guideId]);
    }
  };

  const quickGuides = [
    // ==========================================
    // INTRODUCTION
    // ==========================================
    {
      id: 'handbook-intro',
      category: 'Getting Started',
      title: 'How to Use This Handbook',
      icon: BookOpen,
      color: 'amber',
      teaser: 'Your pocket guide to storytelling mastery',
      content: {
        intro: 'This is the Storyteller\'s Handbook, your quick reference companion for the Art of Story modules. Think of it as your storytelling field guide.',
        blocks: [
          {
            name: '📚 What This Handbook Is',
            tagline: 'Quick reference, not replacement',
            signs: [
              'Distilled principles from the 8 learning modules',
              'Practical techniques you can use immediately',
              'Quick refreshers when you need them',
              'Reference guides for real-world storytelling moments'
            ],
            inAction: 'About to share a story? Check the Five-Part Structure. Struggling with vulnerability? Review our 10% Rule of thumb.',
            quickFix: 'Use these guides as reminders and quick references alongside the modules',
            deeperTruth: 'These guides complement the modules; they don\'t replace them'
          },
          {
            name: '🎯 How to Get the Most From It',
            tagline: 'Strategic, not sequential',
            signs: [
              'You don\'t need to read every guide in order',
              'Jump to what you need when you need it',
              'Revisit guides as you practise the skills',
              'Star your favourites for quick access'
            ],
            inAction: 'Working on Module 3? Check the Oscillating Narratives guide. About to tell a story? Review Delivery Energies.',
            quickFix: 'Use the search and categories to find what you need',
            deeperTruth: 'The best time to use a guide is right before you need the skill'
          },
          {
            name: '⚡ The Learning Journey',
            tagline: 'Modules first, handbook second',
            signs: [
              'Complete the modules for deep learning',
              'Use the handbook for quick refreshers',
              'Practise the techniques in real life',
              'Return to guides when you need reminders'
            ],
            inAction: 'Module → Practice → Handbook → Real Story → Reflect → Repeat',
            quickFix: 'Don\'t skip the modules: that\'s where the real learning happens',
            deeperTruth: 'Reading about storytelling ≠ becoming a storyteller. Practice does.'
          },
          {
            name: '🎨 Research-Informed Techniques',
            tagline: 'Principles, not tricks',
            signs: [
              'Draws on research by Uri Hasson, Paul Zak and Lisa Feldman Barrett',
              'Draws on narrative psychology (Marshall Duke, Dan McAdams)',
              'Research findings and craft advice, with our own rules of thumb labelled as ours',
              'Principles you can adapt, not scripts to memorise'
            ],
            inAction: 'Every technique comes with a "why it works"',
            quickFix: 'These aren\'t manipulation tactics; they\'re ways of helping listeners follow and feel your story',
            deeperTruth: 'Great storytellers understand the science, then make it feel like magic'
          }
        ],
        bottomLine: 'This handbook is your companion, not your teacher. The modules teach. This reminds. You practise. That\'s the path to mastery.',
        challenge: 'Start with the modules. Return here when you need quick guidance. Your future storytelling self will thank you.'
      }
    },

    // ==========================================
    // STORY INVENTORY GUIDES (Module 1)
    // ==========================================
    {
      id: 'six-domains',
      category: 'Story Inventory',
      title: 'The 6 Life Domains Map',
      icon: Target,
      color: 'amber',
      teaser: 'Where your stories live and what they reveal',
      content: {
        intro: 'We sort stories into six life domains (our own map, not a research finding). Mapping yours can reveal patterns you might not see: which domains you over-tell, which you avoid, and what that might say about you.',
        types: [
          {
            name: '🏠 Origin Stories',
            purpose: 'Where you came from, family, formative experiences',
            why: 'Build identity and explain how you became who you are',
            examples: [
              'Family traditions that shaped you',
              'Childhood experiences that stuck',
              'Defining moments from your upbringing',
              'Cultural or geographic roots'
            ],
            magic: 'Origin stories anchor your identity',
            avoid: 'Assuming everyone wants your whole life story'
          },
          {
            name: '💼 Work & Achievement',
            purpose: 'Career, projects, accomplishments, failures',
            why: 'Demonstrate competence and show growth through challenge',
            examples: [
              'Career pivots and turning points',
              'Projects that stretched you',
              'Failures that taught you',
              'Collaborations that changed you'
            ],
            magic: 'Work stories show what you value',
            avoid: 'Humble-bragging or reciting your CV'
          },
          {
            name: '❤️ Relationships',
            purpose: 'Love, friendship, loss, connection',
            why: 'Build emotional bridges and reveal vulnerability',
            examples: [
              'How you met someone important',
              'Friendships that shaped you',
              'Relationship lessons learned',
              'Moments of deep connection or loss'
            ],
            magic: 'Relationship stories create intimacy',
            avoid: 'Oversharing intimate details too early'
          },
          {
            name: '🎯 Challenges & Growth',
            purpose: 'Obstacles overcome, lessons learned, transformation',
            why: 'Build resilience narrative and inspire others',
            examples: [
              'Times you thought you\'d fail but didn\'t',
              'Moments that forced growth',
              'Skills you developed through struggle',
              'Identity shifts after hardship'
            ],
            magic: 'Growth stories show resilience',
            avoid: 'Descending narratives without oscillation'
          },
          {
            name: '✨ Peak Experiences',
            purpose: 'Best days, magical moments, pure joy',
            why: 'Share positive emotion and create aspirational images',
            examples: [
              'Times you felt most alive',
              'Unexpected moments of beauty',
              'Achievements that felt earned',
              'Experiences of awe or wonder'
            ],
            magic: 'Peak stories inspire and energise',
            avoid: 'Making every story a highlight reel'
          },
          {
            name: '🎭 Quirks & Micro-Stories',
            purpose: 'Small observations, funny moments, personality',
            why: 'Build character and create shared delight',
            examples: [
              'Weird things you\'ve noticed',
              'Small absurdities that delighted you',
              'Personal quirks and habits',
              'Brief encounters that stuck'
            ],
            magic: 'Micro-stories reveal personality',
            avoid: 'Confusing "you had to be there" with actually funny'
          }
        ],
        remember: 'Many of us over-tell a few domains and avoid the rest. The avoided domains often hold your most powerful stories.',
        challenge: 'This week: Map 5 stories in each domain. Notice which domains feel easy, which feel scary. That\'s data.'
      }
    },

    {
      id: 'story-spine',
      category: 'Story Structure',
      title: 'The Five-Part Story Structure',
      icon: BookOpen,
      color: 'purple',
      teaser: 'A reliable framework for personal stories',
      content: {
        intro: 'Most memorable personal stories fit this five-part structure. Master it once, adapt it forever.',
        steps: [
          {
            letter: '1',
            word: 'CONTEXT',
            action: 'Set the scene (keep it short: a small slice of your story)',
            details: [
              'Where are we? When is this?',
              'Who are the key players?',
              'What\'s the emotional baseline?',
              'Start as close to the action as possible'
            ],
            example: 'Weak: "So last Tuesday I woke up at 6am..." Strong: "I\'m standing in front of 300 people, microphone in hand, and I\'ve completely forgotten my speech..."',
            whyItWorks: 'Strong context creates immediate orientation without drowning in detail'
          },
          {
            letter: '2',
            word: 'CATALYST',
            action: 'The moment everything changes',
            details: [
              'Something unexpected happens',
              'A choice must be made',
              'External event or internal realisation',
              'This is what the story is actually about'
            ],
            example: '"Then I see her in the front row, the professor I\'d been trying to impress all semester, and she mouths: You got this."',
            whyItWorks: 'The catalyst is where your story actually begins'
          },
          {
            letter: '3',
            word: 'COMPLICATION',
            action: 'Things get harder, stakes rise',
            details: [
              'The obstacle that tests you',
              'Multiple attempts, rising tension',
              'What\'s at stake becomes clear',
              'The valley before the peak'
            ],
            example: '"But I\'m still frozen. Words won\'t come. I can feel the silence stretching..."',
            whyItWorks: 'Complication creates the emotional journey that makes resolution satisfying'
          },
          {
            letter: '4',
            word: 'CLIMAX',
            action: 'The moment of truth',
            details: [
              'The highest point of tension',
              'The choice you made or action you took',
              'Often happens fast after slow buildup',
              'The turn the whole story hinges on'
            ],
            example: '"And then I just... started talking. Not my prepared speech. Just the truth."',
            whyItWorks: 'The climax is what they\'ll remember when they retell your story'
          },
          {
            letter: '5',
            word: 'RESOLUTION',
            action: 'The new normal (The landing)',
            details: [
              'What happened because of your choice',
              'The emotional payoff',
              'What you learned (use sparingly)',
              'The image or feeling you leave them with'
            ],
            example: '"She found me after and said: That was better than any speech. That was real."',
            whyItWorks: 'How you land determines whether they remember your story or forget it'
          }
        ],
        proTip: 'The Cold Open: Start with climax or complication, then backfill context. "So I\'m standing in wet cement up to my knees. Let me explain..."',
        practice: 'Take any story and map it to these five parts. If you can\'t, you might not have a story yet, just a report.',
        warning: 'Weak stories have strong context and weak catalysts. Strong stories reverse this.'
      }
    },

    // ==========================================
    // OSCILLATING NARRATIVES (Module 3)
    // ==========================================
    {
      id: 'oscillating-pattern',
      category: 'Oscillating Narratives',
      title: 'The Oscillating Pattern',
      icon: TrendingUp,
      color: 'blue',
      teaser: 'Our five-step pattern for resilience stories, built on Marshall Duke\'s oscillating family narrative',
      content: {
        intro: 'Marshall Duke considers the oscillating family story (ups and downs, and we stuck together) the most healthful; it hasn\'t been tested as a cause of resilience. The five steps below are this course\'s own way to build one. It\'s not about denying struggle; it\'s about framing growth.',
        steps: [
          {
            letter: '1',
            word: 'BASELINE',
            action: 'The before state',
            details: [
              'How things were before the challenge',
              'What you valued, what felt normal',
              'Not perfect, just the familiar',
              'Keep it brief; don\'t linger here'
            ],
            example: '"I thought I had it figured out. Stable job, good friends, comfortable routine."',
            whyItWorks: 'Establishes contrast for the descent to come'
          },
          {
            letter: '2',
            word: 'DESCENT',
            action: 'The challenge arrives',
            details: [
              'What went wrong, what was lost',
              'The struggle, the uncertainty',
              'Be honest about the difficulty',
              'Don\'t minimise or dramatise'
            ],
            example: '"Then the company folded. Suddenly I\'m 35, unemployed, wondering what I\'d built my life on."',
            whyItWorks: 'Honouring the struggle builds credibility'
          },
          {
            letter: '3',
            word: 'GLIMMER',
            action: 'First sign of possibility',
            details: [
              'The small moment of hope or insight',
              'What you learned in the valley',
              'Could be a person, realisation, or action',
              'Subtle: not a miracle, just a shift'
            ],
            example: '"A friend said: What if this isn\'t loss, it\'s liberation? That question stuck."',
            whyItWorks: 'The oscillation begins, acknowledging agency in difficulty'
          },
          {
            letter: '4',
            word: 'ASCENT',
            action: 'The climb towards growth',
            details: [
              'What you did differently',
              'Skills developed, perspectives shifted',
              'Still hard, but moving forward',
              'Show the work, not just the result'
            ],
            example: '"I started rebuilding, but differently. Less about security, more about meaning."',
            whyItWorks: 'Shows growth through action, not just time passing'
          },
          {
            letter: '5',
            word: 'NEW NORMAL',
            action: 'Changed by the journey',
            details: [
              'How you\'re different now',
              'What you gained (not lost)',
              'Acknowledges both cost and growth',
              'Ends on integration, not triumph'
            ],
            example: '"I don\'t have it all figured out. But I trust my ability to rebuild. That\'s the difference."',
            whyItWorks: 'Oscillating stories end on agency and integration'
          }
        ],
        proTip: 'The key difference, in our five-step model: a descending narrative stops at step 2; an oscillating one goes on to steps 3 to 5. Same events, profoundly different story.',
        practice: 'Take a struggle story you tell and map it: Where does it currently end? How would steps 3-5 reframe it?',
        warning: 'Toxic positivity skips step 2 (descent). Oscillating narratives honour the struggle while highlighting growth.'
      }
    },

    // ==========================================
    // EMOTIONAL ARC & SENSORY DETAILS (Module 5)
    // ==========================================
    {
      id: 'perfect-details',
      category: 'Emotional Arc',
      title: 'The Perfect Details',
      icon: Sparkles,
      color: 'pink',
      teaser: 'Not more details, the RIGHT details',
      content: {
        intro: 'One perfect detail beats a dozen mediocre ones. The more you explain, the less they see. Master storytellers choose sensory details that transport.',
        types: [
          {
            name: '🎯 The Unexpected Specific',
            purpose: 'The detail that breaks pattern, that surprises',
            why: 'Your brain can\'t autofill it, so you have to picture it',
            examples: [
              'Forgettable: "My neighbour is weird"',
              'Memorable: "My neighbour mows his lawn at 2am wearing a headlamp"',
              'Forgettable: "The café was busy"',
              'Memorable: "People were sharing tables with strangers and pretending to be couples"'
            ],
            magic: 'Choose the detail that makes someone go "wait, what?"',
            avoid: 'Generic descriptions that could apply to anything'
          },
          {
            name: '👃 The Sensory Anchor',
            purpose: 'A single well-chosen smell or sound can do more than a paragraph of visual description',
            why: 'Smell has unusually direct links to memory and emotion, and sounds and textures make a scene vivid',
            examples: [
              'Flat: "The lift was broken"',
              'Anchored: "The lift made that grinding noise like a coffee grinder full of rocks"',
              'Flat: "The room was unpleasant"',
              'Anchored: "The room smelled like someone had microwaved fish in a gym sock"'
            ],
            magic: 'Tell them what something sounded like, smelled like, felt like',
            avoid: 'Only describing what things looked like'
          },
          {
            name: '💭 The Emotional Temperature',
            purpose: 'Reveal how someone felt without saying "I felt..."',
            why: 'Showing beats telling, every time',
            examples: [
              'Telling: "I was nervous"',
              'Showing: "I rehearsed my coffee order three times before reaching the counter"',
              'Telling: "She was angry"',
              'Showing: "She smiled the whole time she fired me"'
            ],
            magic: 'Actions and behaviours reveal emotions better than labels',
            avoid: 'Stating emotions directly when you could show them'
          }
        ],
        remember: 'The Rule of Three (a craft habit, not a law): when you list details, two can feel sparse and four crowded, so try three.',
        challenge: 'This week: swap one visual detail per story for a sound, smell or texture.'
      }
    },

    // ==========================================
    // VULNERABILITY CALIBRATION (Module 6)
    // ==========================================
    {
      id: 'ten-percent-rule',
      category: 'Vulnerability',
      title: 'The 10% Rule (Our Rule of Thumb)',
      icon: Heart,
      color: 'rose',
      teaser: 'How much to share, when, and with whom',
      content: {
        intro: 'Vulnerability builds connection. But too much too soon creates discomfort. Too little creates distance. Our 10% Rule, a rule of thumb from this course rather than a research finding, helps you calibrate. It\'s never a reason to share trauma before you\'re ready.',
        steps: [
          {
            letter: '1',
            word: 'ASSESS CONTAINER',
            action: 'Evaluate relationship depth and context',
            details: [
              'How well do you know this person/audience?',
              'What\'s the context? (Work, social, intimate, public)',
              'What\'s their capacity for emotion right now?',
              'Have they shown vulnerability first?'
            ],
            example: 'First date vs. best friend vs. job interview vs. conference talk: each has different vulnerability thresholds',
            whyItWorks: 'Context determines appropriate depth'
          },
          {
            letter: '2',
            word: 'SHARE 10%',
            action: 'Share a little beyond your current level of closeness, never past what feels safe',
            details: [
              'A small step, not a leap',
              'Slightly past comfortable, not deeply exposing',
              'Test the waters before diving in',
              'Notice their response before going deeper'
            ],
            example: 'Instead of "I\'m fine," try "I\'m struggling a bit with this, actually." Then pause.',
            whyItWorks: 'A small step can feel brave to share but easy to receive'
          },
          {
            letter: '3',
            word: 'WATCH RESPONSE',
            action: 'Gauge how they handle your vulnerability',
            details: [
              'Do they lean in or pull back?',
              'Do they share something in return?',
              'Do they minimise or acknowledge?',
              'Do they change subject or go deeper?'
            ],
            example: 'Lean in = "Tell me more about that." Pull back = "Well, everyone struggles sometimes!"',
            whyItWorks: 'Their response shows if deeper sharing is safe'
          },
          {
            letter: '4',
            word: 'ADJUST',
            action: 'Recalibrate based on response',
            details: [
              'Lean in response: Share a little more',
              'Pull back response: Return to lighter sharing',
              'Neutral response: Hold current depth',
              'Match their vulnerability level'
            ],
            example: 'Think of it as emotional improvisation: yes, and',
            whyItWorks: 'Vulnerability is a duet, not a solo'
          }
        ],
        proTip: 'The two extremes: oversharing (too much) and shutting down (too little). Both block connection. The 10% Rule of thumb aims for the middle.',
        practice: 'This week: notice when you overshare and when you shut down completely. Practise small steps instead.',
        warning: 'Vulnerability without boundaries isn\'t brave: it\'s boundary-less. The 10% Rule of thumb helps protect both of you.'
      }
    },

    // ==========================================
    // STORY DELIVERY (Module 7)
    // ==========================================
    {
      id: 'delivery-energies',
      category: 'Story Delivery',
      title: 'The 3 Delivery Energies',
      icon: Mic,
      color: 'cyan',
      teaser: 'Master storytellers shift between these like guitar chords',
      content: {
        intro: 'How you tell a story can make the same material riveting or forgettable.',
        techniques: [
          {
            name: '🤫 The Conspiratorial Whisper',
            when: 'Creating intimacy and drawing listeners in',
            steps: [
              'Lean in slightly',
              'Lower your voice',
              'Make them feel like co-conspirators',
              'Use phrases like "Nobody else knows this part..."'
            ],
            script: '"Between you and me..." [pause] "...this is the part I don\'t usually tell."',
            why: 'Makes them lean in, creates weight and anticipation',
            success: 'They physically lean towards you'
          },
          {
            name: '📈 The Building Momentum',
            when: 'Moving from setup towards climax',
            steps: [
              'Start casual (normal voice, relaxed pace)',
              'Rising action (slightly faster, more animated)',
              'Peak moment (full energy, present tense)',
              'Then... sudden stop before resolution'
            ],
            script: 'Gradually accelerate through complications, then pause dramatically before the climax',
            why: 'Creates anticipation and makes the landing hit harder',
            success: 'They\'re leaning forward, waiting for what happens next'
          },
          {
            name: '⏸️ The Strategic Pause',
            when: 'Before or after the most important moments',
            steps: [
              'Before revelation: "And then I saw who it was..." [2-3 second pause]',
              'After shock: "It was my mother." [pause, let it land]',
              'Before punchline: Let the setup breathe',
              'Hold longer than feels comfortable; to your listeners, it isn\'t too long'
            ],
            script: 'The most powerful tool in storytelling: silence',
            why: 'Pauses create anticipation, let moments land, make them work for meaning',
            success: 'The silence feels electric, not awkward'
          }
        ],
        emergency: {
          title: '🚨 Mid-Story Rescues',
          phrases: [
            'The Energy Injection: "Wait, the crazy part is..." ',
            'The Skip Ahead: "Let me cut to what matters..."',
            'The Interaction: "What would you have done?"',
            'The Honest Abort: "Short version: [punchline]. Anyway..."'
          ],
          lastResort: 'If you\'re losing them, better to land gracefully than crash'
        },
        remember: 'Match your energy to the story\'s emotional temperature. Flat delivery kills even the best story.'
      }
    },

    {
      id: 'vocal-clarity',
      category: 'Story Delivery',
      title: 'The Verbal Parasite Purge',
      icon: Zap,
      color: 'cyan',
      teaser: 'Eliminate the words that dilute your stories',
      content: {
        intro: 'A steady stream of ums can make you sound less sure of yourself, while an occasional one does no harm. These aren\'t character flaws: they\'re habits you can change.',
        hacks: [
          {
            name: '🎤 The Classics',
            move: 'Um, uh, er, you know, like, I mean',
            why: 'Your brain\'s loading screens: verbal placeholder text',
            bonus: 'Replace with pause: Where you\'d say "um," say nothing',
            warning: 'Pauses feel longer to you than they do to your listeners'
          },
          {
            name: '🛡️ The Hedgers',
            move: 'Sort of, kind of, I guess, maybe, probably',
            why: 'Linguistic escape hatches that undermine your authority',
            bonus: 'State with confidence instead of pre-apologising',
            warning: '"I kind of maybe think" = "I\'m terrified you\'ll disagree"'
          },
          {
            name: '📢 The Intensifiers',
            move: 'Literally, actually, basically, honestly, really',
            why: 'When everything is "literally" something, nothing is',
            bonus: 'Use precise words: "Exhausted" not "literally tired"',
            warning: 'Saying "honestly" everywhere implies the rest is dishonest'
          },
          {
            name: '🙏 The Apologisers',
            move: 'Just, sorry but, this might be stupid, does that make sense?',
            why: 'Asking permission to share what you\'re already sharing',
            bonus: 'Remove "just" from sentences completely',
            warning: 'These seem polite but actually undermine you'
          }
        ],
        killers: {
          title: '💀 Why We Use Fillers (common triggers)',
          avoid: [
            'Planning Time: your brain is finding the next words',
            'Silence Terror: fear of gaps in speech',
            'Speed Pressure: mouth moving faster than thoughts'
          ]
        },
        challenge: 'Record yourself telling a 2-minute story and count every filler. Then tell it again and aim for fewer.'
      }
    },

    // ==========================================
    // STORY LISTENING (Module 8)
    // ==========================================
    {
      id: 'empathic-receiving',
      category: 'Story Listening',
      title: 'Empathic Story Receiving',
      icon: Heart,
      color: 'green',
      teaser: 'How to listen without redirecting, fixing, or minimising',
      content: {
        intro: 'As Stephen Covey put it in The 7 Habits of Highly Effective People (1989), most people do not listen with the intent to understand; they listen with the intent to reply. Empathic receiving creates space for others\' stories without hijacking them.',
        steps: [
          {
            letter: '1',
            word: 'PRESENCE',
            action: 'Full attention without agenda',
            details: [
              'Put the phone away (actually away)',
              'Make eye contact in a way that suits the person',
              'Let go of what you\'ll say next',
              'Notice your urge to interrupt, then don\'t'
            ],
            example: 'Not checking the time, not formulating responses, just... present',
            whyItWorks: 'People feel when you\'re truly listening vs. waiting to talk'
          },
          {
            letter: '2',
            word: 'RECEIVE',
            action: 'Let their story land without redirecting',
            details: [
              'Don\'t immediately share your similar story',
              'Don\'t offer solutions unless asked',
              'Don\'t minimise ("It could be worse...")',
              'Don\'t hijack ("That reminds me of when I...")'
            ],
            example: 'Them: "I\'m struggling with..." You: NOT "Oh I had that too!" Just: "Tell me more."',
            whyItWorks: 'Redirecting says "Your story matters less than mine"'
          },
          {
            letter: '3',
            word: 'REFLECT',
            action: 'Show you\'re tracking without inserting yourself',
            details: [
              '"It sounds like..." (reflect feeling)',
              '"So what I\'m hearing is..." (reflect meaning)',
              '"That must have been..." (name the emotion)',
              'Ask questions that go deeper, not wider'
            ],
            example: 'Not: "Have you tried..." But: "What was that like for you?"',
            whyItWorks: 'Reflection shows understanding; advice shows you stopped listening'
          },
          {
            letter: '4',
            word: 'HONOUR',
            action: 'Acknowledge the sharing, not just the content',
            details: [
              '"Thank you for sharing that"',
              '"I appreciate you trusting me with this"',
              '"That took courage to tell"',
              'Sit with the moment before moving on'
            ],
            example: 'Don\'t rush past vulnerable shares to lighten the mood',
            whyItWorks: 'Honouring the act of sharing builds trust for future stories'
          }
        ],
        proTip: 'The Redirect Test: If your response starts with "I" or "You should," pause. Ask a question instead.',
        practice: 'This week: Listen to one story per day without sharing your own. Just receive theirs fully.',
        warning: `Empathic listening is exhausting at first. You're fighting the urge to perform. That's normal. Listening comes first, but if someone may be unsafe or isn't coping, help them connect with support. If you, or the person you're listening to, might be in danger or thinking about suicide, get help now: ${CRISIS_HELP}.`
      }
    },

    {
      id: 'creating-space',
      category: 'Story Listening',
      title: 'Creating Space for Stories',
      icon: Users,
      color: 'green',
      teaser: 'How to invite stories without interrogating',
      content: {
        intro: 'Great storytelling isn\'t just about telling; it\'s about creating containers where others feel safe sharing.',
        techniques: [
          {
            name: '🎯 The Open Invitation',
            when: 'Inviting stories without pressure',
            steps: [
              'Not: "Tell me about..." (commanding)',
              'But: "I\'d love to hear about..." (inviting)',
              'Not: "What happened?" (interrogating)',
              'But: "How did that feel?" (exploring)'
            ],
            script: '"I\'m curious about..." / "I wonder what that was like..."',
            why: 'Invitations create safety; commands create performance pressure',
            success: 'They share willingly, not obligingly'
          },
          {
            name: '👂 The Listening Cues',
            when: 'Showing you\'re tracking without interrupting',
            steps: [
              'Nod at key moments (not constantly)',
              '"Mm" or "Ah" at revelations',
              'Facial expressions that mirror emotion',
              'Lean in during important parts'
            ],
            script: 'Physical presence speaks louder than verbal affirmations',
            why: 'Nonverbal cues signal engagement without interrupting flow',
            success: 'They keep sharing, going deeper'
          },
          {
            name: '🚪 The Permission to Pass',
            when: 'Making it safe to not share',
            steps: [
              '"No pressure to answer..."',
              '"Feel free to pass on this..."',
              '"Only if you\'re comfortable..."',
              'Respect the boundary if they decline'
            ],
            script: 'Paradox: Making it safe to not share makes sharing safer',
            why: 'When people feel they can say no, their yes means more',
            success: 'They share because they want to, not because they have to'
          }
        ],
        remember: 'The best story listeners make telling feel like a gift the teller is giving, not a performance they have to put on.',
        practice: 'This week: Invite one story per day. Notice the difference between asking and inviting.'
      }
    }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            onClick={onClose}
          />

          {/* Quick Guide Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 20 }}
            className="fixed right-0 top-0 bottom-0 w-full md:w-[480px] bg-slate-900 shadow-2xl z-50 overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="p-6 border-b border-slate-800">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                    <Lightbulb className="w-6 h-6 text-yellow-400" />
                    Storyteller's Handbook
                  </h2>
                  <p className="text-sm text-slate-400 mt-1">
                    Your pocket reference for story craft mastery
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-6">
                {!selectedGuide ? (
                  <GuideList
                    guides={quickGuides}
                    onSelectGuide={setSelectedGuide}
                    favouriteGuides={favouriteGuides}
                  />
                ) : (
                  <GuideContent
                    guide={selectedGuide}
                    onBack={() => setSelectedGuide(null)}
                    onFavourite={() => toggleFavourite(selectedGuide.id)}
                    isFavourite={favouriteGuides.includes(selectedGuide.id)}
                    onCopy={copyToClipboard}
                    copiedItem={copiedItem}
                  />
                )}
              </div>
            </div>

            {/* Footer Tip */}
            {!selectedGuide && (
              <div className="p-4 border-t border-slate-800 bg-amber-500/10">
                <p className="text-sm text-amber-300">
                  <Zap className="w-4 h-4 inline mr-1" />
                  <strong>Pro Tip:</strong> These guides sum up key principles from your modules.
                  Use them as quick references when crafting and sharing stories.
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Guide List Component
const GuideList = ({ guides, onSelectGuide, favouriteGuides }) => {
  const categories = [...new Set(guides.map(g => g.category))];

  return (
    <div className="space-y-6">
      {categories.map(category => (
        <div key={category}>
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
            {category}
          </h3>
          <div className="space-y-2">
            {guides
              .filter(g => g.category === category)
              .map(guide => {
                const Icon = guide.icon;
                return (
                  <motion.button
                    key={guide.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => onSelectGuide(guide)}
                    className="w-full p-4 bg-white/5 hover:bg-white/10 rounded-lg transition-all text-left group"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 bg-${guide.color}-500/20 rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-5 h-5 text-${guide.color}-400`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-white font-medium">{guide.title}</h3>
                          {favouriteGuides.includes(guide.id) && (
                            <Star className="w-3 h-3 text-yellow-400 fill-current" />
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{guide.teaser}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-slate-300 transition-colors mt-2" />
                    </div>
                  </motion.button>
                );
              })}
          </div>
        </div>
      ))}
    </div>
  );
};

// Individual Guide Content Component
const GuideContent = ({ guide, onBack, onFavourite, isFavourite, onCopy, copiedItem }) => {
  const renderContent = () => {
    // Render different layouts based on guide structure
    if (guide.content.blocks) {
      return <BlocksLayout content={guide.content} onCopy={onCopy} copiedItem={copiedItem} />;
    } else if (guide.content.steps) {
      return <StepsLayout content={guide.content} onCopy={onCopy} copiedItem={copiedItem} />;
    } else if (guide.content.types) {
      return <TypesLayout content={guide.content} onCopy={onCopy} copiedItem={copiedItem} />;
    } else if (guide.content.techniques) {
      return <TechniquesLayout content={guide.content} onCopy={onCopy} copiedItem={copiedItem} />;
    } else if (guide.content.hacks) {
      return <HacksLayout content={guide.content} onCopy={onCopy} copiedItem={copiedItem} />;
    } else if (guide.content.categories) {
      return <CategoriesLayout content={guide.content} onCopy={onCopy} copiedItem={copiedItem} />;
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-purple-300 hover:text-white transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to handbook
      </button>

      {/* Guide Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-2xl font-bold text-white mb-2">{guide.title}</h3>
            <p className="text-sm text-slate-400">{guide.teaser}</p>
          </div>
          <button
            onClick={onFavourite}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <Star className={`w-5 h-5 ${isFavourite ? 'text-yellow-400 fill-current' : 'text-slate-400'}`} />
          </button>
        </div>
      </div>

      {/* Guide Intro */}
      {guide.content.intro && (
        <Card className="p-4 bg-purple-500/10 border-purple-500/20">
          <p className="text-white">{guide.content.intro}</p>
        </Card>
      )}

      {/* Dynamic Content */}
      {renderContent()}

      {/* Remember / Challenge */}
      {/* Only render these footer elements for guides that DON'T use StepsLayout */}
      {/* StepsLayout already includes proTip, practice, and warning at lines 113-138 */}
      {!guide.content.steps && (
        <>
          {guide.content.remember && typeof guide.content.remember === 'string' && (
            <Card className="p-4 bg-blue-500/10 border-blue-500/20">
              <p className="text-blue-300">
                <Brain className="w-4 h-4 inline mr-1" />
                <strong>Remember:</strong> {guide.content.remember}
              </p>
            </Card>
          )}

          {guide.content.challenge && (
            <Card className="p-4 bg-yellow-500/10 border-yellow-500/20">
              <p className="text-yellow-300">
                <Award className="w-4 h-4 inline mr-1" />
                <strong>Challenge:</strong> {guide.content.challenge}
              </p>
            </Card>
          )}

          {guide.content.proTip && (
            <Card className="p-4 bg-purple-500/10 border-purple-500/20">
              <p className="text-purple-300">
                <Zap className="w-4 h-4 inline mr-1" />
                <strong>Pro Tip:</strong> {guide.content.proTip}
              </p>
            </Card>
          )}

          {guide.content.practice && (
            <Card className="p-4 bg-green-500/10 border-green-500/20">
              <p className="text-green-300">
                <Target className="w-4 h-4 inline mr-1" />
                <strong>Practice:</strong> {guide.content.practice}
              </p>
            </Card>
          )}

          {guide.content.warning && (
            <Card className="p-4 bg-red-500/10 border-red-500/20">
              <p className="text-red-300">
                <MessageSquare className="w-4 h-4 inline mr-1" />
                <strong>Warning:</strong> {guide.content.warning}
              </p>
            </Card>
          )}
        </>
      )}
    </motion.div>
  );
};

export default QuickGuideHub;
